import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

// Horse-racing themed loading messages shown in the overlay
const HORSE_LOADING_MESSAGES: readonly string[] = [
  'Mucking out the stalls',
  'Filling the water buckets',
  'Loading hay bales',
  'Cleaning saddle pads',
  'Polishing bridles',
  'Adjusting the stirrups',
  'Walking the horses',
  'Cleaning the silks',
  'Checking the horseshoes',
  'Moving the starting gate to position',
  'Warming up after the post parade',
  'Practicing the bugle call to post',
  'Harrowing the track surface',
  'Watering down the dirt',
  'Getting the peppermints and carrots ready',
  'Polishing the trophies',
  'Checking the photo-finish camera',
  'Getting the tack ready',
  'Turning for home',
  'In the stretch',
  'Approaching the wire',
  'Making nominations and stakes payments',
  'In the final strides',
  'Ready to spring the latch',
  'Almost home',
  'Just a furlong out',
] as const;

/**
 * Pick a random horse-racing loading message
 * @returns {string} A random loading message
 */
const pickRandomHorseLoadingMessage = (): string => {
  return HORSE_LOADING_MESSAGES[
    Math.floor(Math.random() * HORSE_LOADING_MESSAGES.length)
  ];
};

/**
 * Ref-counted orchestrator for the global horse-loader overlay
 */
@Injectable({ providedIn: 'root' })
export class HorseLoaderService {
  // Hard upper bound (ms) to auto-dismiss a stuck overlay
  private static readonly SAFETY_TIMEOUT_MS = 30_000;

  // Minimum ms to keep the overlay visible to avoid single-frame flashes
  private static readonly MIN_DISPLAY_MS = 600;

  private readonly visibleSubject = new BehaviorSubject<boolean>(false);
  private readonly messageSubject = new BehaviorSubject<string>('');
  private refCount = 0;
  private showStartedAt = 0;
  private safetyTimeoutId?: ReturnType<typeof setTimeout>;
  private pendingHideTimeoutId?: ReturnType<typeof setTimeout>;

  readonly visible$: Observable<boolean> = this.visibleSubject.asObservable();

  readonly message$: Observable<string> = this.messageSubject.asObservable();

  constructor(private readonly zone: NgZone) {}

  /**
   * Show the overlay (ref-count based; arms safety timeout on first call)
   * @param {string} [message] Optional override message
   * @returns {void}
   */
  show(message?: string): void {
    this.cancelPendingHide();

    this.refCount += 1;

    const nextMessage = message && message.trim().length > 0
      ? message
      : pickRandomHorseLoadingMessage();

    this.messageSubject.next(nextMessage);

    if (this.refCount === 1) {
      this.showStartedAt = Date.now();
      this.visibleSubject.next(true);
      this.armSafetyTimeout();
    }
  }

  /**
   * Decrement ref counter; dismiss (with min-display window) at zero
   * @returns {void}
   */
  hide(): void {
    if (this.refCount === 0) return;

    this.refCount -= 1;

    if (this.refCount !== 0) {
      return;
    }

    const elapsed = Date.now() - this.showStartedAt;
    const remaining = HorseLoaderService.MIN_DISPLAY_MS - elapsed;

    if (remaining <= 0) {
      this.dismissNow();
      return;
    }

    // Defer dismiss; re-check refCount so a fresh show() keeps the overlay.
    this.zone.runOutsideAngular(() => {
      this.pendingHideTimeoutId = setTimeout(() => {
        this.zone.run(() => {
          this.pendingHideTimeoutId = undefined;
          if (this.refCount === 0) {
            this.dismissNow();
          }
        });
      }, remaining);
    });
  }

  /**
   * Force-dismiss the overlay regardless of the ref count
   * @returns {void}
   */
  reset(): void {
    this.refCount = 0;
    this.cancelPendingHide();
    this.dismissNow();
  }

  /**
   * Wrap an async function with show/hide (hides even on throw)
   * @template T
   * @param {() => Promise<T>} fn Async work to wrap
   * @param {string} [message] Optional override message
   * @returns {Promise<T>} Result of fn
   */
  async wrap<T>(fn: () => Promise<T>, message?: string): Promise<T> {
    this.show(message);
    try {
      return await fn();
    } finally {
      this.hide();
    }
  }

  /**
   * Hide overlay and disarm the safety timeout (does not touch refCount)
   * @returns {void}
   */
  private dismissNow(): void {
    this.disarmSafetyTimeout();
    this.visibleSubject.next(false);
  }

  /**
   * Cancel a deferred dismiss queued by hide()
   * @returns {void}
   */
  private cancelPendingHide(): void {
    if (this.pendingHideTimeoutId !== undefined) {
      clearTimeout(this.pendingHideTimeoutId);
      this.pendingHideTimeoutId = undefined;
    }
  }

  /**
   * Arm the safety auto-dismiss timer outside Angular's zone
   * @returns {void}
   */
  private armSafetyTimeout(): void {
    this.disarmSafetyTimeout();
    this.zone.runOutsideAngular(() => {
      this.safetyTimeoutId = setTimeout(() => {
        this.zone.run(() => this.reset());
      }, HorseLoaderService.SAFETY_TIMEOUT_MS);
    });
  }

  /**
   * Cancel any in-flight safety timer
   * @returns {void}
   */
  private disarmSafetyTimeout(): void {
    if (this.safetyTimeoutId !== undefined) {
      clearTimeout(this.safetyTimeoutId);
      this.safetyTimeoutId = undefined;
    }
  }
}
